/**
 * projectedInvestigationIds — union of investigation IDs that should
 * mount as InvestigationNode on the Environment projection rail.
 *
 * EnvironmentGraphPage renders InvestigationNode from the rail, not from
 * D3 canvas glyphs. The rail accepts:
 *   - projectable rows from GET /api/v1/investigations/
 *   - investigation-typed nodes from GET
 *     `/api/v1/environment/theatres/{theatre_id}/knowledge-graph`
 *     (D3 `{ id, type, status }` after transform, or raw
 *     `{ node_id, node_type, metadata.status }`)
 *
 * Graph nodes must not re-add IDs the list already excluded as
 * non-projectable. Dismiss keeps `projected_to_environment_at` and the
 * knowledge-graph still returns those investigations.
 *
 * The list is a page (`limit=50`, `next_offset`). Absence from that
 * page is not evidence of eligibility. Graph-only inclusion requires
 * an established projectable status on the node (`status` or
 * `metadata.status` in active|ready|promoted). Missing, idle, dismissed,
 * or unknown status does not admit the ID.
 */

const PROJECTABLE_STATUSES = new Set(['active', 'ready', 'promoted']);

export interface ProjectableInvestigation {
  id?: string | null;
  status?: string | null;
}

export interface GraphInvestigationNode {
  id?: string | null;
  type?: string | null;
  node_id?: string | null;
  node_type?: string | null;
  status?: string | null;
  metadata?: { status?: string | null } | null;
}

export function rawInvestigationId(node: GraphInvestigationNode): string | null {
  const raw = node.id ?? node.node_id ?? null;
  if (!raw) return null;
  return raw.includes(':') ? raw.split(':').slice(1).join(':') : raw;
}

export function isInvestigationGraphNode(node: GraphInvestigationNode): boolean {
  const type = String(node.type ?? node.node_type ?? '').toLowerCase();
  return type === 'investigation';
}

/** Established status from a D3 node or a raw knowledge-graph node. */
export function graphNodeStatus(node: GraphInvestigationNode): string | null {
  const raw = node.status ?? node.metadata?.status;
  if (raw == null || String(raw).trim() === '') return null;
  return String(raw).toLowerCase();
}

export function isProjectableStatus(status: string | null): boolean {
  return status != null && PROJECTABLE_STATUSES.has(status);
}

export function collectProjectedInvestigationIds(input: {
  investigations?: readonly ProjectableInvestigation[];
  graphNodes?: readonly GraphInvestigationNode[];
}): string[] {
  const ids = new Set<string>();
  const excluded = new Set<string>();

  for (const inv of input.investigations ?? []) {
    if (!inv.id) continue;
    if (PROJECTABLE_STATUSES.has(String(inv.status ?? '').toLowerCase())) {
      ids.add(inv.id);
    } else {
      excluded.add(inv.id);
    }
  }

  for (const node of input.graphNodes ?? []) {
    if (!isInvestigationGraphNode(node)) continue;
    const id = rawInvestigationId(node);
    if (!id || excluded.has(id)) continue;
    // Already admitted from a projectable list row — keep it.
    if (ids.has(id)) continue;
    // Graph-only: require established projectable status. A missing
    // list row (partial page, loading, or unavailable) is not eligibility.
    if (isProjectableStatus(graphNodeStatus(node))) {
      ids.add(id);
    }
  }

  return [...ids];
}
